import fetch from "isomorphic-fetch";
import { message, setIsFetching, setUserGuest, setTitleAndScrollToTop, setRoute } from "../../../actions";
import Configuration from "../../../config.js";
import { getQuestionnaireDocuments } from "./Documents";
import ReactGA from "react-ga";
import { translations } from "../../../config";
import { setSurveyCosts, setUsersOverview } from "../../../actions/Users";
import { getQuestions } from "./Questions";
import { getBlocks } from "./Blocks";

/**
 * Konstanten
 */
export const SET_SURVEYS = "SET_SURVEYS";
export const SET_LAST_SURVEY_ANSWER = "SET_LAST_SURVEY_ANSWER";
export const SET_LAST_SURVEY_ANSWERS = "SET_LAST_SURVEY_ANSWERS";
export const SET_NEXT_SURVEY_QUESTION = "SET_NEXT_SURVEY_QUESTION";
export const RESET_BLOCKS = "RESET_BLOCKS";

/**
 * Setter
 */
export function setSurveys( json )
{
	return {
		type: SET_SURVEYS,
		json
	};
}

export function setLastSurveyAnswer( json )
{
	return {
		type: SET_LAST_SURVEY_ANSWER,
		json
	};
}

export function setSurveyAnswers( json )
{
	return {
		type: SET_LAST_SURVEY_ANSWERS,
		json
	};
}

export function setNextSurveyQuestion( json )
{
	return {
		type: SET_NEXT_SURVEY_QUESTION,
		json
	};
}

export function resetBlocks( json )
{
	return {
		type: RESET_BLOCKS,
		json
	};
}

/**
 * Zeigt die gewählte Umfrage und lädt die beantworteten Fragen falls vorhanden
 *
 * @param survey_id {number}
 */
export function getSurveys( survey_id )
{
	return function( dispatch, getState )
	{
		dispatch( setIsFetching( true ) );
		
		// Google Analytics
		if ( localStorage.getItem( "ga" ) )
		{
			ReactGA.event( {
				category: "Survey",
				action: "Umfrage anzeigen"
			} );
		}
		
		const jwt = getState().jwt;
		let headers = jwt !== null ? { "JWT": jwt } : {};
		
		return fetch(
			`${Configuration.apiUrl}/surveys/${survey_id}?${+new Date()}`,
			{
				credentials: "include",
				method: "GET",
				headers
			}
		)
			.then(
				response =>
				{
					if ( response.ok )
					{
						response.json()
							.then( json =>
								{
									dispatch( setTitleAndScrollToTop( `${translations.de_DE.words.survey}: ${json.title}` ) );
									
									// Overview aktualisieren
									if ( !getState().surveys )
									{
										const surveys = Object.assign(
											{},
											{ [json.id]: json }
										);
										dispatch( setSurveys( surveys ) );
									}
									else if ( JSON.stringify( json ) !== JSON.stringify( getState().surveys[json.id] ) )
									{
										const surveys = Object.assign(
											{},
											getState().surveys,
											{ [json.id]: json }
										);
										dispatch( setSurveys( surveys ) );
									}
									
									// Die Dokumente des Fragebogens werden geladen
									dispatch( getQuestionnaireDocuments( json.questionnaire_id ) );
									dispatch( getSurveyQuestions( json ) );
								}
							);
					}
					else
					{
						response.json()
							.then( json =>
								{
									dispatch( message( json.error, "error" ) );
								}
							);
					}
					
					dispatch( setIsFetching( false ) );
				}
			)
			.catch(
				error =>
				{
					dispatch( message( "Der Dienst steht derzeit nicht zur Verfügung. Bitte versuchen Sie es später erneut.", "error" ) );
					dispatch( setIsFetching( false ) );
				}
			);
	};
	
} // function getSurveys(...)

/**
 * Lädt die gewählte Umfrage im gewählten Format herunter
 *
 * @param a_survey {Object}
 * @param a_format {string}
 */
export function downloadSurvey( a_survey, a_format = "pdf" )
{
	return function( dispatch, getState )
	{
		dispatch( setIsFetching( true ) );
		
		// Google Analytics
		if ( localStorage.getItem( "ga" ) )
		{
			ReactGA.event( {
				category: "Survey",
				action: "Umfrage exportieren"
			} );
		}
		
		const { jwt, questionnairesOverview } = { ...getState() };
		
		let headers = jwt !== null ? { "JWT": jwt } : {};
		
		return fetch(
			`${Configuration.apiUrl}/surveys/${a_survey.id}?format=${a_format}&${+new Date()}`,
			{
				credentials: "include",
				method: "GET",
				headers
			}
		)
			.then(
				response =>
				{
					if ( response.ok )
					{
						switch ( a_format )
						{
							case "pdf":
								response.blob().then( blob =>
									{
										let newBlob = new Blob( [ blob ], { type: "application/pdf" } );
										const questionnaire = questionnairesOverview[a_survey.questionnaire_id];
										const fileName = `${translations.de_DE.words.survey} ${a_survey.title} ${questionnaire.law} ${questionnaire.title}.pdf`;
										
										if ( window.navigator.msSaveOrOpenBlob )
										{
											window.navigator.msSaveOrOpenBlob( newBlob, fileName );
										}
										else
										{
											let url = URL.createObjectURL( newBlob );
											let a = document.createElement( "a" );
											document.body.appendChild( a );
											a.style = "display: none";
											a.href = url;
											a.download = fileName;
											a.click();
											
											URL.revokeObjectURL( url );
										}
										dispatch( setIsFetching( false ) );
									}
								);
								break;
							default:
								break;
						}
						
					}
					else
					{
						response.json()
							.then( json =>
								{
									dispatch( message( json.error, "error" ) );
									dispatch( setIsFetching( false ) );
								}
							);
					}
				}
			)
			.catch(
				error =>
				{
					dispatch( message( "Der Dienst steht derzeit nicht zur Verfügung. Bitte versuchen Sie es später erneut.", "error" ) );
					dispatch( setIsFetching( false ) );
				}
			);
	};
	
} // function downloadSurvey(...)

/**
 * Zeigt alle Fragebögen in der Übersicht an
 */
export function getSurveyOverview( reset = true )
{
	return function( dispatch, getState )
	{
		dispatch( setIsFetching( true ) );
		
		// Google Analytics
		if ( localStorage.getItem( "ga" ) )
		{
			ReactGA.event( {
				category: "Survey",
				action: "Umfragenübersicht laden"
			} );
		}
		
		const jwt = getState().jwt;
		let headers = jwt !== null ? { "JWT": jwt } : {};
		
		return fetch(
			`${Configuration.apiUrl}/surveys?${+new Date()}`,
			{
				credentials: "include",
				method: "GET",
				headers
			}
		)
			.then(
				response =>
				{
					if ( response.ok )
					{
						response.json()
							.then( json =>
								{
									// Reset
									if ( reset )
									{
										dispatch( resetBlocks( {} ) );
										dispatch( setSurveyAnswers( null ) );
										dispatch( setNextSurveyQuestion( null ) );
										dispatch( setUsersOverview( null ) );
									}
									
									dispatch( setSurveys( json ) );
								}
							);
					}
					else
					{
						response.json()
							.then( json =>
								{
									dispatch( message( json.error, "error" ) );
								}
							);
					}
					
					dispatch( setIsFetching( false ) );
				}
			)
			.catch(
				error =>
				{
					dispatch( message( "Der Dienst steht derzeit nicht zur Verfügung. Bitte versuchen Sie es später erneut.", "error" ) );
					dispatch( setIsFetching( false ) );
					
					// setze Gastnutzer Erkennung
					dispatch( setUserGuest() );
				}
			);
	};
	
} // function getSurveyOverview(...)

/**
 * Api-Anfrage: Neuen Umfrage erstellen, im Store ablegen und erste Frage laden
 *
 * @param a_formData {Object}
 * @param a_mode {string}
 */
export function createSurveys( a_formData, a_mode = "production" )
{
	return function( dispatch, getState )
	{
		dispatch( setIsFetching( true ) );
		
		// Google Analytics
		if ( localStorage.getItem( "ga" ) )
		{
			ReactGA.event( {
				category: "Survey",
				action: "Umfrage anlegen"
			} );
		}
		
		const jwt = getState().jwt;
		const user = getState().user;
		const questionnaires = getState().questionnairesOverview;
		let headers = jwt !== null ? { "JWT": jwt } : {};
		headers["content-type"] = "application/json";
		
		return fetch(
			`${Configuration.apiUrl}/surveys${a_mode === "test" ? "-test" : ""}?${+new Date()}`,
			{
				credentials: "include",
				method: "POST",
				body: JSON.stringify( a_formData ),
				headers
			}
		)
			.then(
				response =>
				{
					// Umfrage angelegt
					if ( response.ok )
					{
						// Fragebogen laden
						response.json()
							.then( id =>
								{
									const survey_article_id = a_mode === "test" ? 0 : questionnaires[a_formData.questionnaire_id].article_id;
									const survey = {
										...a_formData,
										id: id.toString(),
										organization_id: user.organization.id,
										user_id: user.id.toString(),
										sex: user.sex,
										surname: user.surname,
										forename: user.forename,
										survey_article_id: survey_article_id,
										completed_at: null,
										created_at: new Date().toISOString().slice( 0, 19 )
									};
									
									let surveys = {};
									
									if ( a_mode !== "test" )
									{
										// Recherchenkosten aktualisieren
										dispatch( setSurveyCosts( parseFloat( user.organization.survey_costs ) + parseFloat( questionnaires[a_formData.questionnaire_id].price ) ) );
										
										// Overview aktualisieren
										surveys = Object.assign(
											{},
											getState().surveys,
											{ [id]: survey }
										);
									}
									
									if ( a_mode === "test" )
									{
										sessionStorage.setItem( "Survey", JSON.stringify( survey ) );
										
										dispatch( setSurveyAnswers( null ) );
										dispatch( getQuestionnaireDocuments( a_formData.questionnaire_id ) );
										dispatch( getFirstQuestion( a_formData.questionnaire_id ) );
										dispatch( getQuestions( a_formData.questionnaire_id ) );
										dispatch( getBlocks( a_formData.questionnaire_id ) );
										
										// Overview aktualisieren
										surveys = Object.assign(
											{},
											getState().surveys,
											{ 0: survey }
										);
									}
									
									dispatch( setSurveys( surveys ) );
									
									// Umfragetitel setzen
									dispatch( setTitleAndScrollToTop( `${translations.de_DE.words.survey}: ${a_formData.title}` ) );
									
									// Erst hier soll die Anzeige verändert werden, damit beim Laden die Umfrage bereits angelegt wurde!
									dispatch( setRoute( `${translations.de_DE.routes[a_mode === "test" ? "surveyTest" : "survey"]}/${id}` ) );
									
									// Der Nutzer soll ein Alert erhalten, dass die Anfrage erfolgreich war.
									dispatch( message( `${a_mode === "test" ? "Der Test wurde für die " : ""}${translations.de_DE.words.survey} erfolgreich angelegt.`, "info", 5000 ) );
								}
							);
					}
					// Eingabefehler oder Serverfehler
					else
					{
						response.json()
							.then( json =>
								{
									// Der Nutzer erfährt warum die Anfrage fehlgeschlagen ist.
									dispatch( message( json.error, "error" ) );
								}
							);
					}
					
					dispatch( setIsFetching( false ) );
				}
			)
			.catch(
				error =>
				{
					dispatch( message( "Der Dienst steht derzeit nicht zur Verfügung. Bitte versuchen Sie es später erneut.", "error" ) );
					dispatch( setIsFetching( false ) );
				}
			);
	};
	
} // function createSurveys(...)

/**
 * Api-Anfrage: erste Frage laden
 *
 * @param questionnaire_id {number}
 */
export function getFirstQuestion( questionnaire_id )
{
	return function( dispatch, getState )
	{
		dispatch( setIsFetching( true ) );
		
		// Google Analytics
		if ( localStorage.getItem( "ga" ) )
		{
			ReactGA.event( {
				category: "Survey",
				action: "Erste Frage aus der Umfrage laden"
			} );
		}
		
		const jwt = getState().jwt;
		let headers = jwt !== null ? { "JWT": jwt } : {};
		
		return fetch(
			`${Configuration.apiUrl}/surveys/${questionnaire_id}/first-question-id?${+new Date()}`,
			{
				credentials: "include",
				method: "GET",
				headers
			}
		)
			.then(
				response =>
				{
					if ( response.ok )
					{
						response.json()
							.then( question_id =>
								{
									if ( question_id > 0 )
									{
										dispatch( getNextSurveyQuestion( questionnaire_id, question_id ) );
									}
									else
									{
										dispatch( message( "Es wurde keine Frage gefunden.", "error" ) );
									}
								}
							);
						
					}
					// Eingabefehler oder Serverfehler
					else
					{
						response.json()
							.then( json =>
								{
									// Der Nutzer erfährt warum die Anfrage fehlgeschlagen ist.
									dispatch( message( json.error, "error" ) );
								}
							);
					}
					
					dispatch( setIsFetching( false ) );
				}
			)
			.catch(
				error =>
				{
					dispatch( message( "Der Dienst steht derzeit nicht zur Verfügung. Bitte versuchen Sie es später erneut.", "error" ) );
					dispatch( setIsFetching( false ) );
				}
			);
	};
	
} // function getFirstQuestion(...)

/**
 * Api-Anfrage: nachfolgende Frage laden
 *
 * @param questionnaire_id {number}
 * @param question_id {number}
 */
export function getNextSurveyQuestion( questionnaire_id, question_id )
{
	return function( dispatch, getState )
	{
		dispatch( setIsFetching( true ) );
		
		// Google Analytics
		if ( localStorage.getItem( "ga" ) )
		{
			ReactGA.event( {
				category: "Survey",
				action: "Nächste Frage aus der Umfrage laden"
			} );
		}
		
		const jwt = getState().jwt;
		let headers = jwt !== null ? { "JWT": jwt } : {};
		
		return fetch(
			`${Configuration.apiUrl}/admin/questionnaires/${questionnaire_id}/questions/${question_id}?${+new Date()}`,
			{
				credentials: "include",
				method: "GET",
				headers
			}
		)
			.then(
				response =>
				{
					if ( response.ok )
					{
						response.json()
							.then( json =>
								{
									dispatch( setNextSurveyQuestion( json ) );
								}
							);
						
					}
					// Eingabefehler oder Serverfehler
					else
					{
						response.json()
							.then( json =>
								{
									// Der Nutzer erfährt warum die Anfrage fehlgeschlagen ist.
									dispatch( message( json.error, "error" ) );
								}
							);
					}
					
					dispatch( setIsFetching( false ) );
				}
			)
			.catch(
				error =>
				{
					dispatch( message( "Der Dienst steht derzeit nicht zur Verfügung. Bitte versuchen Sie es später erneut.", "error" ) );
					dispatch( setIsFetching( false ) );
				}
			);
	};
	
} // function getNextQuestionId()

/**
 * Api-Anfrage: Frage beantworten
 *
 * @param survey {Object}
 * @param question {Object}
 */
export function answerSurveyQuestion( survey, question )
{
	return function( dispatch, getState )
	{
		dispatch( setIsFetching( true ) );
		
		// Google Analytics
		if ( localStorage.getItem( "ga" ) )
		{
			ReactGA.event( {
				category: "Survey",
				action: "Frage beantworten"
			} );
		}
		
		const jwt = getState().jwt;
		let headers = jwt !== null ? { "JWT": jwt } : {};
		
		let formData = new FormData();
		formData.append( "question_id", question.id );
		
		if ( typeof question.reasoning !== "undefined" && question.reasoning !== null )
		{
			formData.append( "question_reasoning", question.reasoning );
		}
		formData.append( "answer_id", question.answer_id );
		
		if ( survey.id > 0 )
		{
			return fetch(
				`${Configuration.apiUrl}/surveys/${survey.id}/answers`,
				{
					credentials: "include",
					method: "POST",
					body: formData,
					headers
				}
			)
				.then(
					response =>
					{
						if ( response.ok )
						{
							
							response.json()
								.then( id =>
									{
										dispatch( setLastSurveyAnswer( { ...question, id: question.id.toString(), survey_answer_id: id.toString() } ) );
										
										if ( question.next_question_id !== null )
										{
											dispatch( setNextSurveyQuestion( getState().questions[survey.questionnaire_id][question.next_question_id] ) );
										}
										else
										{
											dispatch( setNextSurveyQuestion( null ) );
										}
										
									}
								);
						}
						// Eingabefehler oder Serverfehler
						else
						{
							response.json()
								.then( json =>
									{
										// Der Nutzer erfährt warum die Anfrage fehlgeschlagen ist.
										dispatch( message( json.error, "error" ) );
									}
								);
						}
						
						dispatch( setIsFetching( false ) );
					}
				)
				.catch(
					error =>
					{
						dispatch( message( "Der Dienst steht derzeit nicht zur Verfügung. Bitte versuchen Sie es später erneut.", "error" ) );
						dispatch( setIsFetching( false ) );
					}
				);
		}
		else
		{
			const surveyAnswers = getState().surveyAnswers;
			const survey_answer_id = surveyAnswers ? Object.keys( surveyAnswers ).length : 0;
			
			dispatch( setLastSurveyAnswer( { ...question, id: question.id.toString(), survey_answer_id: survey_answer_id } ) );
			
			if ( question.next_question_id !== null )
			{
				dispatch( setNextSurveyQuestion( getState().questions[survey.questionnaire_id][question.next_question_id] ) );
			}
			else
			{
				dispatch( setNextSurveyQuestion( null ) );
			}
			
			dispatch( setIsFetching( false ) );
		}
	};
	
} // answerSurveyQuestion()

/**
 * Api-Anfrage: Frage beantworten
 *
 * @param survey {Object}
 * @param question {Object}
 */
export function patchSurveyQuestionReasoning( survey, question )
{
	return function( dispatch, getState )
	{
		dispatch( setIsFetching( true ) );
		
		// Google Analytics
		if ( localStorage.getItem( "ga" ) )
		{
			ReactGA.event( {
				category: "Survey",
				action: "Antwort begründen"
			} );
		}
		
		const jwt = getState().jwt;
		let headers = jwt !== null ? { "JWT": jwt } : {};
		headers["content-type"] = "application/json";
		
		if ( survey.id > 0 )
		{
			return fetch(
				`${Configuration.apiUrl}/surveys/${survey.id}/reasoning`,
				{
					credentials: "include",
					method: "PATCH",
					headers,
					body: JSON.stringify( {
						question_id: question.id,
						question_reasoning: question.reasoning
					} )
				}
			)
				.then(
					response =>
					{
						if ( response.ok )
						{
							dispatch( setLastSurveyAnswer( { ...question, reasoning: question.reasoning } ) );
						}
						// Eingabefehler oder Serverfehler
						else
						{
							response.json()
								.then( json =>
									{
										// Der Nutzer erfährt warum die Anfrage fehlgeschlagen ist.
										dispatch( message( json.error, "error" ) );
									}
								);
						}
						
						dispatch( setIsFetching( false ) );
					}
				)
				.catch(
					error =>
					{
						dispatch( message( "Der Dienst steht derzeit nicht zur Verfügung. Bitte versuchen Sie es später erneut.", "error" ) );
						dispatch( setIsFetching( false ) );
					}
				);
		}
		else
		{
			dispatch( setIsFetching( false ) );
		}
	};
	
} // patchSurveyQuestionReasoning()

/**
 * Api-Anfrage: Antworten zurücksetzen und Frage erneut beantworten
 *
 * @param survey {Object}
 * @param question {Object}
 */
export function changeSurveyQuestionAnswer( survey, question )
{
	return function( dispatch, getState )
	{
		dispatch( setIsFetching( true ) );
		
		// Google Analytics
		if ( localStorage.getItem( "ga" ) )
		{
			ReactGA.event( {
				category: "Survey",
				action: "Antwort ändern"
			} );
		}
		
		const jwt = getState().jwt;
		let headers = jwt !== null ? { "JWT": jwt } : {};
		
		if ( survey.id > 0 )
		{
			// Die Antwort wird mitgeschickt damit die Frage aktualisiert werden kann.
			return fetch(
				`${Configuration.apiUrl}/surveys/${survey.id}/answers/${question.id}?answer_id=${question.answer_id}`,
				{
					credentials: "include",
					method: "DELETE",
					headers
				}
			)
				.then(
					response =>
					{
						if ( response.ok )
						{
							// Alle Fragen mit einer Antwort-Id über der übergebenen Frage werden aus dem State gelöscht
							Object.keys( getState().surveyAnswers ).forEach(
								( a_survey_answer_id ) =>
								{
									if ( a_survey_answer_id > question.survey_answer_id )
									{
										delete getState().surveyAnswers[a_survey_answer_id];
									}
								}
							);
							
							// Die nächste Frage wird entsprechend neu gesetzt
							dispatch( setLastSurveyAnswer( { ...question, id: question.id.toString(), survey_answer_id: question.survey_answer_id.toString() } ) );
							
							if ( question.next_question_id !== null )
							{
								dispatch( setNextSurveyQuestion( getState().questions[survey.questionnaire_id][question.next_question_id] ) );
							}
							else
							{
								dispatch( setNextSurveyQuestion( null ) );
							}
						}
						else
						{
							response.json()
								.then( json =>
									{
										// Der Nutzer erfährt warum die Anfrage fehlgeschlagen ist.
										dispatch( message( json.error, "error" ) );
									}
								);
						}
						
						dispatch( setIsFetching( false ) );
					}
				)
				.catch(
					error =>
					{
						dispatch( message( "Der Dienst steht derzeit nicht zur Verfügung. Bitte versuchen Sie es später erneut.", "error" ) );
						dispatch( setIsFetching( false ) );
					}
				);
		}
		else
		{
			// Alle Fragen mit einer Antwort-Id über der übergebenen Frage werden aus dem State gelöscht
			Object.keys( getState().surveyAnswers ).forEach(
				( a_survey_answer_id ) =>
				{
					if ( a_survey_answer_id > question.survey_answer_id )
					{
						delete getState().surveyAnswers[a_survey_answer_id];
					}
				}
			);
			
			// Die nächste Frage wird entsprechend neu gesetzt
			dispatch( setLastSurveyAnswer( { ...question, id: question.id.toString(), survey_answer_id: question.survey_answer_id.toString() } ) );
			
			if ( question.next_question_id !== null )
			{
				dispatch( setNextSurveyQuestion( getState().questions[survey.questionnaire_id][question.next_question_id] ) );
			}
			else
			{
				dispatch( setNextSurveyQuestion( null ) );
			}
			
			dispatch( setIsFetching( false ) );
		}
	};
	
} // changeSurveyQuestionAnswer()

/**
 * Zeigt die gewählte Umfrage mit den aktuellen Bearbeitungsstand
 *
 * @param survey {Object}
 */
export function getSurveyQuestions( survey )
{
	return function( dispatch, getState )
	{
		dispatch( setIsFetching( true ) );
		
		// Google Analytics
		if ( localStorage.getItem( "ga" ) )
		{
			ReactGA.event( {
				category: "Survey",
				action: "Umfrage zum Bearbeiten laden"
			} );
		}
		
		const jwt = getState().jwt;
		let headers = jwt !== null ? { "JWT": jwt } : {};
		
		return fetch(
			`${Configuration.apiUrl}/surveys/${survey.id}/questions?${+new Date()}`,
			{
				credentials: "include",
				method: "GET",
				headers
			}
		)
			.then(
				response =>
				{
					if ( response.ok )
					{
						response.json()
							.then( json =>
								{
									// Wenn keine Antworten vorliegen, dann die erste Frage holen und die letzten Antworten im Store löschen
									if ( Object.keys( json ).length === 0 )
									{
										dispatch( setSurveyAnswers( null ) );
										dispatch( getFirstQuestion( survey.questionnaire_id ) );
									}
									else
									{
										dispatch( setSurveyAnswers( json ) );
										
										const lastAnswer = json[Object.keys( json ).pop()];
										if ( lastAnswer !== undefined )
										{
											// Nächste Frage laden falls das Ende nicht erreicht wurde
											const questions = getState().blocksByQuestionnaireId[survey.questionnaire_id][lastAnswer.block_id]["questions"];
											const answers = questions[lastAnswer.id]["answers"];
											if ( answers[lastAnswer.answer_id].next_question_id !== null )
											{
												dispatch( getNextSurveyQuestion( survey.questionnaire_id, lastAnswer["next_question_id"] ) );
											}
										}
									}
								}
							);
						
					}
					// Eingabefehler oder Serverfehler
					else
					{
						response.json()
							.then( json =>
								{
									// Der Nutzer erfährt warum die Anfrage fehlgeschlagen ist.
									dispatch( message( json.error, "error" ) );
								}
							);
					}
					
					dispatch( setIsFetching( false ) );
				}
			)
			.catch(
				error =>
				{
					dispatch( message( "Der Dienst steht derzeit nicht zur Verfügung. Bitte versuchen Sie es später erneut.", "error" ) );
					dispatch( setIsFetching( false ) );
				}
			);
	};
	
} // getSurveyQuestions()

/**
 * Api-Anfrage: Umfrage abschließen
 *
 * @param survey_id {number}
 */
export function completeSurvey( survey_id )
{
	return function( dispatch, getState )
	{
		dispatch( setIsFetching( true ) );
		
		// Google Analytics
		if ( localStorage.getItem( "ga" ) )
		{
			ReactGA.event( {
				category: "Survey",
				action: "Umfrage abschließen"
			} );
		}
		
		const jwt = getState().jwt;
		let headers = jwt !== null ? { "JWT": jwt } : {};
		headers["content-type"] = "application/json";
		
		return fetch(
			`${Configuration.apiUrl}/surveys/${survey_id}/complete?${+new Date()}`,
			{
				credentials: "include",
				method: "PATCH",
				headers,
				body: JSON.stringify( survey_id )
			}
		)
			.then(
				response =>
				{
					// Umfrage aktualisiert
					if ( response.ok )
					{
						response.json()
							.then( json =>
								{
									// Datensatz anpassen
									const survey = { ...getState().surveys[survey_id], completed_at: json };
									
									// Overview aktualisieren
									const surveys = Object.assign(
										{},
										getState().surveys,
										{ [survey_id]: survey }
									);
									dispatch( setSurveys( surveys ) );
								}
							);
						
						// Der Nutzer soll ein Alert erhalten, dass die Anfrage erfolgreich war.
						dispatch( message( `${translations.de_DE.words.survey} erfolgreich abgeschlossen.`, "info", 5000 ) );
					}
					// Eingabefehler oder Serverfehler
					else
					{
						response.json()
							.then( json =>
								{
									// Der Nutzer erfährt warum die Anfrage fehlgeschlagen ist.
									dispatch( message( json.error, "error" ) );
								}
							);
					}
					
					dispatch( setIsFetching( false ) );
				}
			)
			.catch(
				error =>
				{
					dispatch( message( "Der Dienst steht derzeit nicht zur Verfügung. Bitte versuchen Sie es später erneut.", "error" ) );
					dispatch( setIsFetching( false ) );
				}
			);
	};
	
} // function completeSurvey(...)

/**
 * Api-Anfrage: Umfrage löschen
 *
 * @param id {number}
 */
export function deleteSurveys( id )
{
	return function( dispatch, getState )
	{
		dispatch( setIsFetching( true ) );
		
		// Google Analytics
		if ( localStorage.getItem( "ga" ) )
		{
			ReactGA.event( {
				category: "Survey",
				action: "Umfrage löschen"
			} );
		}
		
		const jwt = getState().jwt;
		let headers = jwt !== null ? { "JWT": jwt } : {};
		
		return fetch(
			`${Configuration.apiUrl}/surveys/${id}`,
			{
				credentials: "include",
				method: "DELETE",
				headers
			}
		)
			.then(
				response =>
				{
					if ( response.ok )
					{
						dispatch( setSurveyAnswers( null ) );
						
						// Umfrage aus dem State löschen
						Object.keys( getState().surveys ).forEach(
							( a_id ) =>
							{
								if ( a_id.toString() === id.toString() )
								{
									delete getState().surveys[id];
								}
							}
						);
						
						// Der Nutzer soll ein Alert erhalten, dass die Anfrage erfolgreich war.
						dispatch( message( `${translations.de_DE.words.survey} erfolgreich gelöscht.`, "info", 5000 ) );
					}
					else
					{
						response.json()
							.then( json =>
								{
									// Der Nutzer erfährt warum die Anfrage fehlgeschlagen ist.
									dispatch( message( json.error, "error" ) );
								}
							);
					}
					
					dispatch( setIsFetching( false ) );
				}
			)
			.catch(
				error =>
				{
					dispatch( message( "Der Dienst steht derzeit nicht zur Verfügung. Bitte versuchen Sie es später erneut.", "error" ) );
					dispatch( setIsFetching( false ) );
				}
			);
	};
	
} // function deleteSurveys(...)